import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, X, ShoppingCart, User, LogOut, Menu } from 'lucide-react';
import { usePatientAuth } from '../../context/PatientAuthContext';
import { useCart } from '../../context/CartContext';
import { getCurrentUserRole } from '../../utils/userRoleCheck';
import { broadcastLogout, listenCrossTabLogout } from '../../utils/crossTabAuthSync';
import AuthGateModal from '../AuthGateModal';
import './Navbar.css';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [showLoginPopup, setShowLoginPopup] = React.useState(false);
  const [authGate, setAuthGate] = React.useState({ open: false, redirectTo: '/book' });
  const location = useLocation();
  const navigate = useNavigate();
  const { patient, logout: logoutPatient, isAuthenticated } = usePatientAuth();
  const { cartCount, isCartBouncing, isBadgePopping } = useCart();
  const [currentUser, setCurrentUser] = React.useState(null);

  // Lock body & html scroll when mobile menu is open
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
      document.documentElement.classList.add('mobile-menu-open');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('mobile-menu-open');
      document.documentElement.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
      document.documentElement.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Close active dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.nav-item.dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleBookNow = () => {
    const userRole = getCurrentUserRole();
    if (userRole.isStaff) {
      setAuthGate({ open: true, redirectTo: '/tests' });
      return;
    }
    if (isAuthenticated || userRole.isPatient) {
      navigate('/tests');
    } else {
      setAuthGate({ open: true, redirectTo: '/tests' });
    }
  };

  React.useEffect(() => {
    // Check if user metadata exists in localStorage
    const roleInfo = getCurrentUserRole();
    if (roleInfo && roleInfo.role !== 'guest') return;

    // Check if the popup was already shown in this session
    const hasBeenShown = sessionStorage.getItem('loginPopupShown');
    if (hasBeenShown === 'true') return;

    const timer = setTimeout(() => {
      // Re-verify login status before triggering the modal
      const current = getCurrentUserRole();
      if (!current || current.role === 'guest') {
        setShowLoginPopup(true);
        sessionStorage.setItem('loginPopupShown', 'true');
      }
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, []);

  const updateCurrentUserFromLocalStorage = React.useCallback(() => {
    const roleInfo = getCurrentUserRole();
    if (roleInfo && roleInfo.role !== 'guest') {
      let parsedUser = {};
      const userKey =
        roleInfo.role === 'super_admin' ? (localStorage.getItem('admin_user') ? 'admin_user' : 'portal_user') :
        roleInfo.role === 'lab_owner' || roleInfo.role === 'lab_manager' ? (localStorage.getItem('lab_user') ? 'lab_user' : (localStorage.getItem('portal_user') ? 'portal_user' : 'user')) :
        roleInfo.role === 'phlebotomist' ? (localStorage.getItem('phleb_user') ? 'phleb_user' : 'portal_user') :
        roleInfo.role === 'employer' ? (localStorage.getItem('employer_user') ? 'employer_user' : 'hub_user') :
        roleInfo.role === 'diagnostic' ? (localStorage.getItem('diag_user') ? 'diag_user' : 'portal_user') :
        roleInfo.role === 'research' ? (localStorage.getItem('res_user') ? 'res_user' : 'research_user') :
        roleInfo.role === 'doctor' ? (localStorage.getItem('doctor_user') ? 'doctor_user' : 'portal_user') :
        roleInfo.role === 'lab_technician' ? (localStorage.getItem('technician_user') ? 'technician_user' : 'portal_user') :
        (localStorage.getItem('patient_user') ? 'patient_user' : (localStorage.getItem('user') ? 'user' : 'portal_user'));

      try {
        const raw = localStorage.getItem(userKey);
        if (raw) parsedUser = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch (e) {}

      const displayName =
        parsedUser.name ||
        (parsedUser.first_name ? `${parsedUser.first_name} ${parsedUser.last_name || ''}`.trim() : '') ||
        roleInfo.name ||
        parsedUser.email ||
        roleInfo.email ||
        roleInfo.label;

      setCurrentUser({
        user: { ...parsedUser, name: displayName, email: parsedUser.email || roleInfo.email },
        role: roleInfo.role,
        dashboard: roleInfo.dashboard,
        label: roleInfo.label,
        displayName: displayName
      });
      return;
    }
    setCurrentUser(null);
  }, []);

  React.useEffect(() => {
    updateCurrentUserFromLocalStorage();

    window.addEventListener('local-storage-update', updateCurrentUserFromLocalStorage);
    window.addEventListener('storage', updateCurrentUserFromLocalStorage);
    const unsubscribeCrossTab = listenCrossTabLogout(() => {
      updateCurrentUserFromLocalStorage();
    });
    return () => {
      window.removeEventListener('local-storage-update', updateCurrentUserFromLocalStorage);
      window.removeEventListener('storage', updateCurrentUserFromLocalStorage);
      unsubscribeCrossTab();
    };
  }, [isAuthenticated, patient, updateCurrentUserFromLocalStorage]);

  // Derive profile picture from current user's role
  const profilePicture = React.useMemo(() => {
    if (!currentUser) return null;
    if (currentUser.role === 'patient') return patient?.profile_picture || null;
    if (currentUser.role === 'phlebotomist') {
      try {
        const u = JSON.parse(localStorage.getItem('phleb_user') || '{}');
        return u.profile_picture || null;
      } catch { return null; }
    }
    return null;
  }, [currentUser, patient]);

  const handleLogout = () => {
    const role = currentUser?.role || 'user';
    if (role === 'patient') {
      try { logoutPatient(); } catch(e){}
    }
    broadcastLogout(role);
    setCurrentUser(null);
  };

  // Reset state on route change
  React.useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  if (location.pathname.startsWith('/portal')) return null;

  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <>
      {isMobileMenuOpen && (
        <div 
          className="mobile-menu-backdrop" 
          onClick={() => setIsMobileMenuOpen(false)} 
        />
      )}

      <nav className="navbar glass">
        <div className="navbar-container">
          <Link to="/" className="navbar-logo">
            <img src="/images/MusB_Diagnostic_Logo.webp" alt="MusB Logo" className="logo-img" width="239" height="84" />
          </Link>

          <div className="mobile-header-actions">
            <button 
              id="navbar-cart-btn-mobile"
              type="button" 
              className={`nav-cart-btn mobile-header-cart-btn ${isCartBouncing ? 'is-bouncing' : ''}`}
              onClick={() => navigate('/cart')}
              title="Shopping Cart"
              aria-label="Shopping Cart"
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && <span className={`nav-cart-badge ${isBadgePopping ? 'is-popping' : ''}`}>{cartCount}</span>}
            </button>

            <button 
              className={`nav-toggle ${isMobileMenuOpen ? 'active' : ''}`} 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              {isMobileMenuOpen
                ? <X size={24} color="#0b3051" strokeWidth={2.5} />
                : <Menu size={24} color="#0b3051" strokeWidth={2} />}
            </button>
          </div>

          <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
            <li className="nav-item">
              <Link to="/" className={`nav-link ${isActive('/')}`}>Home</Link>
            </li>
            <li className="nav-item">
              <Link to="/mobile-phlebotomy" className={`nav-link ${isActive('/mobile-phlebotomy')}`}>Mobile Phlebotomy</Link>
            </li>
            <li className="nav-item">
              <Link to="/test-catalog" className={`nav-link ${isActive('/test-catalog')}`}>Catalog</Link>
            </li>
            <li className="nav-item">
              <Link to="/self-pay-lab-tests" className={`nav-link ${isActive('/self-pay-lab-tests')}`}>Self-Pay Patients</Link>
            </li>
            <li className="nav-item">
              <Link to="/employer-health-program" className={`nav-link ${isActive('/employer-health-program')}`}>Employers & HR</Link>
            </li>
            
            <li 
              className={`nav-item dropdown ${activeDropdown === 'facilities' ? 'mobile-open' : ''}`}
              onMouseEnter={() => setActiveDropdown('facilities')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <div 
                className="nav-link dropdown-toggle" 
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown(activeDropdown === 'facilities' ? null : 'facilities');
                }}
              >
                Facilities <ChevronDown size={14} className={`dropdown-arrow ${activeDropdown === 'facilities' ? 'open' : ''}`} />
              </div>
              <ul className={`dropdown-menu ${activeDropdown === 'facilities' ? 'show' : ''}`}>
                <li><Link to="/assisted-living-testing" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Assisted Living</Link></li>
                <li><Link to="/community-programs" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Non Profits</Link></li>
                <li><Link to="/physicians" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Physician</Link></li>
              </ul>
            </li>

            <li 
              className={`nav-item dropdown ${activeDropdown === 'research' ? 'mobile-open' : ''}`}
              onMouseEnter={() => setActiveDropdown('research')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <div 
                className="nav-link dropdown-toggle" 
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown(activeDropdown === 'research' ? null : 'research');
                }}
              >
                Research <ChevronDown size={14} className={`dropdown-arrow ${activeDropdown === 'research' ? 'open' : ''}`} />
              </div>
              <ul className={`dropdown-menu ${activeDropdown === 'research' ? 'show' : ''}`}>
                <li><Link to="/research-central-lab" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Research Central Lab</Link></li>
                <li><Link to="/early-diagnostics" className="dropdown-item" onClick={() => setActiveDropdown(null)}>Diagnostic Validation</Link></li>
              </ul>
            </li>

            <li className="nav-item">
              <Link to="/offers" className={`nav-link ${isActive('/offers')}`}>Offers</Link>
            </li>
            <li className="nav-item">
              <button 
                type="button" 
                className="nav-link nav-download-trigger"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('musb-open-app-promo'));
                  setIsMobileMenuOpen(false);
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                title="Download MusB Diagnostics App"
              >
                Download App
              </button>
            </li>
            <li className="nav-item">
              <Link to="/contact" className={`nav-link ${isActive('/contact')}`}>Contact Us</Link>
            </li>
            <li className="nav-item mobile-only">
              <Link 
                to="/cart" 
                className={`nav-link ${isActive('/cart')} mobile-menu-cart-link`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mobile-cart-label-wrap">
                  <ShoppingCart size={18} /> Cart
                </span>
                {cartCount > 0 ? (
                  <span className="mobile-menu-cart-count">{cartCount} {cartCount === 1 ? 'item' : 'items'}</span>
                ) : (
                  <span className="mobile-menu-cart-empty">0 items</span>
                )}
              </Link>
            </li>

            {currentUser ? (
              <li className="nav-item mobile-only" style={{ width: '100%', margin: '0.4rem 0' }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  padding: '12px 14px',
                  background: 'rgba(99, 102, 241, 0.04)',
                  border: '1px solid rgba(99, 102, 241, 0.15)',
                  borderRadius: '14px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                    <Link to={currentUser.dashboard} onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 0 }}>
                      {profilePicture ? (
                        <img 
                          src={profilePicture} 
                          alt="Profile" 
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '1.5px solid rgba(99, 102, 241, 0.5)',
                            flexShrink: 0
                          }}
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          color: 'white',
                          flexShrink: 0
                        }}>
                          {(currentUser.displayName || currentUser.label).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {currentUser.displayName || currentUser.label}
                      </span>
                    </Link>
                    <span className="mobile-user-role-pill">{currentUser.label}</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: '100%',
                      background: 'rgba(239, 68, 68, 0.08)',
                      border: '1px solid rgba(239, 68, 68, 0.25)',
                      color: '#ef4444',
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              </li>
            ) : (
              <li className="nav-item mobile-only">
                <Link to="/portal/login" className="nav-link mobile-user-link">
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <User size={18} /> Login / Sign Up
                  </span>
                </Link>
              </li>
            )}
            <li className="nav-item mobile-only" style={{ width: '100%', marginTop: '0.4rem' }}>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleBookNow();
                }}
                style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', fontWeight: 700, borderRadius: '10px' }}
              >
                Book Now
              </button>
            </li>
          </ul>
    
          <div className="nav-actions">
            <button 
              id="navbar-cart-btn"
              type="button" 
              className={`nav-cart-btn ${isCartBouncing ? 'is-bouncing' : ''}`}
              onClick={() => navigate('/cart')}
              title="Shopping Cart"
            >
              <ShoppingCart size={18} />
              {cartCount > 0 && <span className={`nav-cart-badge ${isBadgePopping ? 'is-popping' : ''}`}>{cartCount}</span>}
            </button>
            <button className="btn btn-primary" onClick={handleBookNow}>Book Now</button>
            {currentUser ? (
              <div className="user-nav-info">
                <Link 
                  to={currentUser.dashboard} 
                  className="user-nav-badge" 
                  title={`Go to ${currentUser.label} Dashboard`}
                >
                  {profilePicture ? (
                    <img 
                      src={profilePicture} 
                      alt="Profile" 
                      className="user-nav-avatar"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="user-nav-initial">
                      {(currentUser.displayName || currentUser.label).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="user-nav-text">
                    <span className="user-nav-name">
                      {(currentUser.displayName || currentUser.label).split(' ')[0]}
                    </span>
                    <span className="user-nav-role">{currentUser.label}</span>
                  </div>
                </Link>
                <button 
                  onClick={handleLogout} 
                  className="btn-logout-icon"
                  title="Log out"
                  type="button"
                >
                  <LogOut size={15} />
                </button>
              </div>
            ) : (
              <Link to="/portal/login" className="btn btn-outline">Login</Link>
            )}
          </div>
        </div>
      </nav>

      {showLoginPopup && (
        <div className="login-popup-overlay" onClick={() => setShowLoginPopup(false)}>
          <div className="login-popup-card" onClick={(e) => e.stopPropagation()}>
            <button className="login-popup-close-btn" onClick={() => setShowLoginPopup(false)}>
              <X size={18} />
            </button>
            <div className="login-popup-logo">
              <img src="/images/MusB_Diagnostic_Logo.webp" alt="MusB Logo" loading="lazy" />
            </div>
            <h3>Welcome to MusB Diagnostics</h3>
            <p>Log in to easily manage appointments, track your phlebotomist in real-time, and access your lab results securely.</p>
            <div className="login-popup-actions">
              <Link to="/portal/login" className="btn-popup-login" onClick={() => setShowLoginPopup(false)}>
                Login / Sign Up
              </Link>
              <button className="btn-popup-dismiss" onClick={() => setShowLoginPopup(false)}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
      <AuthGateModal
        open={authGate.open}
        onClose={() => setAuthGate(g => ({ ...g, open: false }))}
        redirectTo={authGate.redirectTo}
        onGuest={() => navigate(authGate.redirectTo)}
      />
    </>
  );
};

export default Navbar;